// client/src/components/Toast/ToastProvider.tsx

import * as RadixToast from '@radix-ui/react-toast';
import * as React from 'react';
import { useNavigate } from 'react-router';
import type { ShowToastFn, ShowToastInput, ToastMessage } from './types';
import './toast.scss';

interface ToastContextValue {
  showToast: ShowToastFn;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);
ToastContext.displayName = 'ToastContext';

const DEFAULT_DURATION_MS = 6000;
const MAX_VISIBLE = 3;

export const ToastProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [queue, setQueue] = React.useState<ToastMessage[]>([]);
  const navigate = useNavigate();

  const dismiss = React.useCallback((id: string) => {
    setQueue((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = React.useCallback<ShowToastFn>((input: ShowToastInput) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const next: ToastMessage = { id, ...input };
    setQueue((prev) => {
      const merged = [...prev, next];
      // FIFO eviction when over cap
      return merged.length > MAX_VISIBLE
        ? merged.slice(merged.length - MAX_VISIBLE)
        : merged;
    });
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({ showToast }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {queue.map((toast) => (
          <RadixToast.Root
            key={toast.id}
            className={
              toast.variant === 'warning'
                ? 'toast-root toast-root--warning'
                : 'toast-root'
            }
            duration={toast.duration ?? DEFAULT_DURATION_MS}
            onOpenChange={(open) => {
              if (!open) dismiss(toast.id);
            }}
          >
            <div
              className={
                toast.href ? 'toast-body toast-body--clickable' : 'toast-body'
              }
              onClick={() => {
                if (toast.href) {
                  navigate(toast.href);
                  dismiss(toast.id);
                }
              }}
              role={toast.href ? 'button' : undefined}
              tabIndex={toast.href ? 0 : undefined}
              onKeyDown={(e) => {
                if (toast.href && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  navigate(toast.href);
                  dismiss(toast.id);
                }
              }}
            >
              <RadixToast.Title className="toast-title">
                {toast.title}
              </RadixToast.Title>
              <RadixToast.Description className="toast-description">
                {toast.body}
              </RadixToast.Description>
            </div>
            <RadixToast.Close
              className="toast-close"
              aria-label="Dismiss notification"
            >
              ×
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="toast-viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
};

export function useToastContextOrThrow(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast must be used inside a <ToastProvider>. ' +
        'Did you forget to wrap your tree?',
    );
  }
  return ctx;
}
